import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { resolvePromoBarStyles, formatPromoMessage } from '../lib/promoBarTemplates';
import { useLanguage } from '../hooks/useLanguage';
import { resolvePromoMessage } from '../lib/siteLanguages';

const ADMIN_PATH = '/admin-secret-wownautic-2024';

function parseEndMs(endAt) {
  if (!endAt) return null;
  const s = String(endAt).trim();
  const iso = s.endsWith('Z') ? s : `${s}Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function formatCountdown(msLeft) {
  if (msLeft <= 0) return '00:00:00';
  const totalSec = Math.floor(msLeft / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (days > 0) {
    return `${days}j ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const PromoBar = ({ previewConfig = null, previewLanguage = null }) => {
  const location = useLocation();
  const { language } = useLanguage();
  const displayLanguage = previewLanguage || language;
  const [config, setConfig] = useState(null);
  const [active, setActive] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const barRef = useRef(null);

  const isAdminRoute = location.pathname.startsWith(ADMIN_PATH);

  useEffect(() => {
    if (previewConfig) {
      setConfig(previewConfig);
      setActive(true);
      return undefined;
    }

    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    let cancelled = false;

    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/promo-bar`);
        if (cancelled) return;
        if (res.data?.active && res.data?.config) {
          setConfig(res.data.config);
          setActive(true);
        } else {
          setConfig(null);
          setActive(false);
        }
      } catch {
        if (!cancelled) {
          setConfig(null);
          setActive(false);
        }
      }
    };

    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [previewConfig, location.pathname]);

  useEffect(() => {
    if (!active || !config?.show_countdown) return undefined;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active, config?.show_countdown]);

  const styles = useMemo(() => (config ? resolvePromoBarStyles(config) : null), [config]);

  const endMs = useMemo(() => parseEndMs(config?.end_at), [config?.end_at]);
  const countdown = endMs ? formatCountdown(endMs - nowMs) : null;

  const message = useMemo(() => {
    if (!config) return '';
    return formatPromoMessage(resolvePromoMessage(config, displayLanguage), {
      code: config.coupon_code,
      percent: config.discount_percent,
      discount: config.discount_percent,
    });
  }, [config, displayLanguage]);

  useEffect(() => {
    if (!active || !config || !styles) {
      document.documentElement.style.setProperty('--wwn-promo-bar-height', '0px');
      return undefined;
    }
    const el = barRef.current;
    if (!el) return undefined;

    const syncHeight = () => {
      document.documentElement.style.setProperty('--wwn-promo-bar-height', `${el.offsetHeight}px`);
    };

    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);
    window.addEventListener('resize', syncHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncHeight);
      document.documentElement.style.setProperty('--wwn-promo-bar-height', '0px');
    };
  }, [active, config, styles, message, countdown, displayLanguage]);

  if (isAdminRoute && !previewConfig) return null;
  if (!active || !config || !styles) return null;

  const fontSize = Math.max(styles.font_size_px - 1, 12);

  return (
    <div
      ref={barRef}
      role="region"
      aria-label="Promotion"
      className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center box-border px-3 py-2.5 sm:py-2 text-center shadow-md"
      style={{
        minHeight: `${styles.height_px}px`,
        background: styles.background,
        borderBottom: styles.border_css === 'none' ? undefined : styles.border_css,
        color: styles.text_color,
        fontSize: `${fontSize}px`,
      }}
    >
      <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-2 gap-y-1.5 max-w-6xl w-full">
        <span className="font-medium leading-snug w-full sm:w-auto">{message}</span>
        {config.coupon_code ? (
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 font-bold tracking-wide"
            style={{ backgroundColor: styles.accent_color, color: '#0B1120' }}
          >
            <Tag className="w-3.5 h-3.5" aria-hidden />
            {config.coupon_code}
          </span>
        ) : null}
        {config.show_countdown && countdown ? (
          <span
            className="font-mono font-semibold tabular-nums shrink-0"
            style={{ color: styles.accent_color }}
          >
            {countdown}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default PromoBar;
